/**
 * SupportWidgetCore — framework-agnostic vanilla-TS support widget core (D-08).
 *
 * Responsibilities:
 * - Install continuous log capture (console + fetch) from start() onward
 * - On submit(firstMessage): fetch a hub-ticket from the ECU API, open a /customer WS,
 *   and emit SESSION_CREATE with the context bundle + log snapshot + firstMessage.
 *
 * No React dependency — this module is safe to import without React being present.
 * The React wrapper lives in src/react/index.tsx (Plan 04).
 */
import { io, Socket } from 'socket.io-client';
import { SUPPORT_EVENTS, WS_NAMESPACES } from '@support-system/shared';
import type { SessionCreatePayload } from '@support-system/shared';
import { LogBuffer } from './log-buffer';
import { installConsoleCapture } from './console-capture';
import { installFetchCapture } from './fetch-capture';
import { mergeById } from './merge-by-id';

/** Idle window after the last keystroke before emitting typing.stop (Pitfall 6). */
const TYPING_IDLE_MS = 3000;

/**
 * A single uploaded attachment (CHAT-06, D-32) as carried on `ChatMessage.attachments`. `s3Key`
 * is never sent over the wire — download URLs are signed LAZILY on render (D-34) via
 * `getAttachmentDownloadUrl`.
 */
export interface ChatAttachment {
    id: number;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
}

/** Inbound 'message' broadcast shape (FanoutSubscriber — same wire shape on both namespaces). */
export interface ChatMessage {
    id: number;
    content: string;
    isRead: boolean;
    createdAt: string;
    participant: {
        id: number;
        authorType: 'customer' | 'agent' | 'system' | 'ai';
        kind: 'human' | 'machine';
        displayName: string;
        avatarUrl: string | null;
        externalUserId: string | null;
    };
    /** Attachments linked to this message (CHAT-06, D-32) — empty/absent for text-only messages. */
    attachments?: ChatAttachment[];
}

export interface SupportWidgetCoreConfig {
    /** Base URL of the ECU API (e.g. http://localhost:3000/api/v1) */
    apiUrl: string;
    /** Base URL of the hub WS server (e.g. http://localhost:3001) */
    hubUrl: string;
    /** Returns the current JWT token, or null if not authenticated */
    getToken: () => string | null;
    /** Open host-configured context map (PKG-05) */
    context: Record<string, unknown>;
    /** Optional translation helper (unused in Phase 3 — reserved for Phase 4 UX) */
    translate?: (key: string) => string;
}

const SUBMIT_TIMEOUT_MS = 10_000;

export class SupportWidgetCore {
    private readonly _config: SupportWidgetCoreConfig;
    private readonly _buffer: LogBuffer = new LogBuffer();
    private _disposeConsole: (() => void) | null = null;
    private _disposeFetch: (() => void) | null = null;
    private _started = false;

    /** The /customer socket, retained after submit() so chat rides the same connection. */
    private _socket: Socket | null = null;
    /** The conversation created by submit() — required for sendMessage/join. */
    private _conversationId: number | null = null;
    /**
     * Id-keyed map of every message seen so far (live 'message' events + 'history' refetches),
     * shared across mergeById calls so a refetch never duplicates or drops a live frame
     * received during the race (CHAT-08, Pitfall 4, D-36).
     */
    private readonly _messagesById: Map<number, ChatMessage> = new Map();
    /** Registered via onHistory() — invoked with the full merged+sorted set on every refetch. */
    private _onHistory: ((messages: ChatMessage[]) => void) | null = null;
    /** Cleanup for the connect/visibilitychange listeners registered by _watchReconnect(). */
    private _disposeReconnectWatch: (() => void) | null = null;

    // Debounce state for the customer's own typing.start/typing.stop emission (Pitfall 6).
    private _typingActive = false;
    private _typingStopTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(config: SupportWidgetCoreConfig) {
        this._config = config;
    }

    /**
     * Start continuous log capture. Idempotent — safe to call multiple times.
     */
    start(): void {
        if (this._started) return;
        this._started = true;
        this._disposeConsole = installConsoleCapture((entry) => this._buffer.pushConsole(entry));
        this._disposeFetch = installFetchCapture((entry) => this._buffer.pushNetwork(entry));
    }

    /**
     * Submit a help request.
     * 1. POST hub-ticket from ECU API
     * 2. Open /customer WS with the ticket
     * 3. Emit SESSION_CREATE with context + log snapshot + firstMessage
     * 4. Resolve with the new conversationId when hub acks { ok: true, conversationId },
     *    reject on error or timeout.
     *
     * The socket is RETAINED on `this._socket` (D-20) — chat (sendMessage/onMessage) rides
     * this same connection. Callers must NOT open a second /customer socket.
     */
    async submit(firstMessage?: string): Promise<number> {
        const { context } = this._config;
        const socket = await this._openTicketSocket();

        return new Promise<number>((resolve, reject) => {
            const timer = setTimeout(() => {
                socket.disconnect();
                this._socket = null;
                reject(new Error('session.create timed out'));
            }, SUBMIT_TIMEOUT_MS);

            socket.on('connect', () => {
                // 3. Emit SESSION_CREATE with payload
                const payload: SessionCreatePayload = {
                    context,
                    logs: this._buffer.snapshot(),
                    firstMessage,
                };

                socket.emit(
                    SUPPORT_EVENTS.SESSION_CREATE,
                    payload,
                    (ack: { ok: boolean; conversationId?: number; error?: string }) => {
                        clearTimeout(timer);
                        if (ack?.ok && ack.conversationId != null) {
                            this._conversationId = ack.conversationId;
                            // Join the conversation room so 'message' broadcasts arrive on
                            // this same socket (D-20 — persistent thread).
                            socket.emit('join', { conversationId: ack.conversationId });
                            // Reconnect catch-up (CHAT-08, D-35/D-37): re-join + refetch history
                            // on every 'connect' (incl. reconnects) and on tab refocus.
                            this._watchReconnect(socket, ack.conversationId);
                            resolve(ack.conversationId);
                        } else {
                            reject(new Error(ack?.error ?? 'session.create ack error'));
                        }
                        // Keep socket open — chat (sendMessage/onMessage) rides this connection.
                        // Caller is responsible for disposing via SupportWidgetCore.dispose().
                    },
                );
            });

            socket.on('connect_error', (err: Error) => {
                clearTimeout(timer);
                socket.disconnect();
                this._socket = null;
                reject(err);
            });
        });
    }

    /**
     * Fetch a fresh hub-ticket from the ECU API and open the /customer WS with it. Retained on
     * `this._socket` (D-20) — shared by submit() and reconnect(). Does NOT join a room or emit
     * session.create/history — callers do that once 'connect' fires.
     */
    private async _openTicketSocket(): Promise<Socket> {
        const { apiUrl, hubUrl, getToken } = this._config;
        const token = getToken();

        const ticketRes = await fetch(`${apiUrl}/support/hub-ticket`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!ticketRes.ok) {
            throw new Error(`hub-ticket fetch failed: ${ticketRes.status}`);
        }

        const { hubTicket } = (await ticketRes.json()) as { hubTicket: string };

        const socket = io(hubUrl + WS_NAMESPACES.CUSTOMER, {
            auth: { hubTicket },
            transports: ['websocket'],
        });
        this._socket = socket;
        return socket;
    }

    /**
     * Reconnect to a PREVIOUSLY-CREATED conversation (D-21, Pitfall 5 — the caller reads
     * `support_conversation_<identity.id>` from localStorage and passes the stored id).
     *
     * Opens a fresh /customer socket (a new hub-ticket, since the widget does not persist the
     * old one), joins the conversation room, and refetches full history (CHAT-02/CHAT-08,
     * D-35). Registers the same connect/visibilitychange watch as submit() (D-37). Resolves
     * with the merged+sorted message set so the caller can render it immediately, or rejects
     * if the ticket/socket cannot be established (the caller falls back to
     * support.help.chat.historyFailed, D-26).
     */
    async reconnect(conversationId: number): Promise<ChatMessage[]> {
        const socket = await this._openTicketSocket();

        return new Promise<ChatMessage[]>((resolve, reject) => {
            const timer = setTimeout(() => {
                socket.disconnect();
                this._socket = null;
                reject(new Error('reconnect timed out'));
            }, SUBMIT_TIMEOUT_MS);

            socket.on('connect', () => {
                clearTimeout(timer);
                this._conversationId = conversationId;
                socket.emit('join', { conversationId });
                this._watchReconnect(socket, conversationId);
                this._refetchHistory(conversationId).then(resolve, reject);
            });

            socket.on('connect_error', (err: Error) => {
                clearTimeout(timer);
                socket.disconnect();
                this._socket = null;
                reject(err);
            });
        });
    }

    /**
     * Send a chat message on the retained /customer socket (CHAT-01). Requires submit() to
     * have completed (the conversation must exist). Identity/participant resolution happens
     * server-side from the handshake-bound ticket claims (T-04-02-impersonate) — only
     * {conversationId, content, attachmentIds} are sent. `attachmentIds` (CHAT-06, D-32) come
     * from prior `uploadAttachment` calls.
     */
    sendMessage(content: string, attachmentIds?: number[]): Promise<{ ok: boolean; error?: string }> {
        if (!this._socket || this._conversationId == null) {
            return Promise.resolve({ ok: false, error: 'no active conversation' });
        }
        // Sending implies the customer stopped typing (Pitfall 6).
        this._stopTyping();
        const conversationId = this._conversationId;
        return new Promise((resolve) => {
            this._socket!.emit(
                SUPPORT_EVENTS.MESSAGE_SEND,
                { conversationId, content, attachmentIds },
                (ack: { ok: boolean; error?: string }) => resolve(ack),
            );
        });
    }

    /**
     * Upload a single file for the active conversation (CHAT-06, D-28/D-30): emits
     * `attachment.presign` over the retained /customer socket to obtain a presigned PUT URL +
     * unlinked attachment row, then PUTs the file bytes DIRECTLY to S3 (the hub never touches
     * the bytes, D-28). The `Content-Type` header on the PUT MUST match `file.type` byte-for-
     * byte (Pitfall 2) — it is replayed exactly from the value sent to `attachment.presign`, or
     * S3 returns 403 SignatureDoesNotMatch. Resolves with the new `attachmentId` to pass to
     * `sendMessage()`, or `{ ok: false, error }` on validation/network failure.
     */
    uploadAttachment(file: File): Promise<{ ok: true; attachmentId: number } | { ok: false; error: string }> {
        if (!this._socket || this._conversationId == null) {
            return Promise.resolve({ ok: false, error: 'no active conversation' });
        }
        const conversationId = this._conversationId;
        const socket = this._socket;

        return new Promise((resolve) => {
            socket.emit(
                'attachment.presign',
                {
                    conversationId,
                    filename: file.name,
                    contentType: file.type,
                    sizeBytes: file.size,
                },
                async (ack: { ok: boolean; attachmentId?: number; uploadUrl?: string; error?: string }) => {
                    if (!ack?.ok || !ack.uploadUrl || ack.attachmentId == null) {
                        resolve({ ok: false, error: ack?.error ?? 'presign failed' });
                        return;
                    }
                    try {
                        const putRes = await fetch(ack.uploadUrl, {
                            method: 'PUT',
                            headers: { 'Content-Type': file.type },
                            body: file,
                        });
                        if (!putRes.ok) {
                            resolve({ ok: false, error: 'upload failed' });
                            return;
                        }
                        resolve({ ok: true, attachmentId: ack.attachmentId });
                    } catch {
                        resolve({ ok: false, error: 'upload failed' });
                    }
                },
            );
        });
    }

    /**
     * Lazily resolve a presigned GET download URL for `attachmentId` (D-34) over the retained
     * /customer socket — called on render/click, never eagerly for a whole history.
     */
    getAttachmentDownloadUrl(attachmentId: number): Promise<{ ok: true; downloadUrl: string } | { ok: false; error: string }> {
        if (!this._socket || this._conversationId == null) {
            return Promise.resolve({ ok: false, error: 'no active conversation' });
        }
        const conversationId = this._conversationId;
        return new Promise((resolve) => {
            this._socket!.emit(
                'attachment.download-url',
                { conversationId, attachmentId },
                (ack: { ok: boolean; downloadUrl?: string; error?: string }) => {
                    if (ack?.ok && ack.downloadUrl) {
                        resolve({ ok: true, downloadUrl: ack.downloadUrl });
                    } else {
                        resolve({ ok: false, error: ack?.error ?? 'failed to resolve download url' });
                    }
                },
            );
        });
    }

    /**
     * Subscribe to incoming 'message' broadcasts (FanoutSubscriber — the sole broadcast
     * point) on the retained /customer socket. Returns an unsubscribe function; cleanup via
     * socket.off, never disconnect (the socket is shared with submit()/sendMessage()).
     *
     * Each message is also upserted into the shared id-keyed map (CHAT-08, Pitfall 4) so a
     * later _refetchHistory() never duplicates or drops a live frame.
     */
    onMessage(cb: (message: ChatMessage) => void): () => void {
        if (!this._socket) {
            return () => {};
        }
        const socket = this._socket;
        const handler = (msg: ChatMessage) => {
            mergeById(this._messagesById, [msg]);
            cb(msg);
        };
        socket.on('message', handler);
        return () => {
            socket.off('message', handler);
        };
    }

    /**
     * Register the callback invoked with the full merged+sorted message set on every history
     * refetch (initial reopen, reconnect, tab refocus — CHAT-02/CHAT-08, D-35).
     */
    onHistory(cb: (messages: ChatMessage[]) => void): void {
        this._onHistory = cb;
    }

    /**
     * Subscribe to the relayed typing.start/typing.stop events (CHAT-04, transient — never
     * persisted) on the retained /customer socket. Only the agent side is relevant here (the
     * customer's own typing state is local UI, not echoed back). The callback receives `true`
     * on typing.start and `false` on typing.stop; cleanup via socket.off, never disconnect.
     */
    onTyping(cb: (typing: boolean) => void): () => void {
        if (!this._socket) {
            return () => {};
        }
        const socket = this._socket;
        const onStart = (payload: { conversationId: number; authorType: string }) => {
            if (payload.authorType !== 'agent') return;
            cb(true);
        };
        const onStop = (payload: { conversationId: number; authorType: string }) => {
            if (payload.authorType !== 'agent') return;
            cb(false);
        };
        socket.on(SUPPORT_EVENTS.TYPING_START, onStart);
        socket.on(SUPPORT_EVENTS.TYPING_STOP, onStop);
        return () => {
            socket.off(SUPPORT_EVENTS.TYPING_START, onStart);
            socket.off(SUPPORT_EVENTS.TYPING_STOP, onStop);
        };
    }

    /**
     * Subscribe to the message.read fanout (CHAT-05) — flips the local message's isRead so
     * the customer's own sent bubbles update their ticks (FanoutSubscriber is the sole
     * broadcast point; this method never flips isRead itself). Also updates the shared
     * id-keyed map so a later history refetch keeps the flipped state (Pitfall 4).
     */
    onRead(cb: (payload: { messageId: number; conversationId: number; isRead: boolean }) => void): () => void {
        if (!this._socket) {
            return () => {};
        }
        const socket = this._socket;
        const handler = (payload: { messageId: number; conversationId: number; isRead: boolean }) => {
            const existing = this._messagesById.get(payload.messageId);
            if (existing) {
                this._messagesById.set(payload.messageId, { ...existing, isRead: payload.isRead });
            }
            cb(payload);
        };
        socket.on(SUPPORT_EVENTS.MESSAGE_READ, handler);
        return () => {
            socket.off(SUPPORT_EVENTS.MESSAGE_READ, handler);
        };
    }

    /**
     * Stop the customer's own typing.start state (emits typing.stop if currently active).
     */
    private _stopTyping(): void {
        if (this._typingStopTimer) {
            clearTimeout(this._typingStopTimer);
            this._typingStopTimer = null;
        }
        if (!this._socket || this._conversationId == null || !this._typingActive) {
            return;
        }
        this._typingActive = false;
        this._socket.emit(SUPPORT_EVENTS.TYPING_STOP, { conversationId: this._conversationId });
    }

    /**
     * Debounced typing.start/typing.stop emitter (CHAT-04, Pitfall 6 — typing.start emits ONCE
     * per burst; typing.stop emits after ~3s idle or on send). Call on every composer
     * keystroke. No-op if no conversation exists yet.
     */
    notifyTyping(): void {
        if (!this._socket || this._conversationId == null) {
            return;
        }
        if (!this._typingActive) {
            this._typingActive = true;
            this._socket.emit(SUPPORT_EVENTS.TYPING_START, { conversationId: this._conversationId });
        }
        if (this._typingStopTimer) {
            clearTimeout(this._typingStopTimer);
        }
        this._typingStopTimer = setTimeout(() => this._stopTyping(), TYPING_IDLE_MS);
    }

    /**
     * Emit message.read {conversationId, messageId} (CHAT-05) when the customer views an
     * incoming (agent-authored) message. The isRead flip itself arrives back via onRead()
     * (FanoutSubscriber is the sole broadcast point).
     */
    markRead(messageId: number): void {
        if (!this._socket || this._conversationId == null) {
            return;
        }
        this._socket.emit(SUPPORT_EVENTS.MESSAGE_READ, {
            conversationId: this._conversationId,
            messageId,
        });
    }

    /**
     * Refetch the full ordered history for `conversationId` over the retained /customer socket
     * via the 'history' WS request (T-04-03-history-leak — guarded server-side by
     * WsTenantGuard; no agent JWT available to the widget, A1/Open Q2). Upserts the result into
     * the SAME id-keyed map live 'message' events write to (Pitfall 4 — never replace) and
     * notifies onHistory() with the merged, sorted set. Full refetch, not since-id (D-35).
     */
    _refetchHistory(conversationId: number): Promise<ChatMessage[]> {
        if (!this._socket) {
            return Promise.resolve([]);
        }
        return new Promise((resolve) => {
            this._socket!.emit('history', { conversationId }, (messages: ChatMessage[]) => {
                const merged = mergeById(this._messagesById, messages ?? []);
                this._onHistory?.(merged);
                resolve(merged);
            });
        });
    }

    /**
     * Reconnect catch-up (CHAT-08, D-35/D-37, Pattern 3): Socket.IO v4 fires 'connect' on BOTH
     * the initial connect AND every reconnect. Re-join the room (server-side join state does
     * not survive a reconnect) and refetch full history. Also refetch on tab refocus
     * (visibilitychange). No visible "reconnecting" indicator (D-37). Idempotent — re-calling
     * replaces any prior watch.
     */
    private _watchReconnect(socket: Socket, conversationId: number): void {
        this._disposeReconnectWatch?.();

        const onConnect = () => {
            socket.emit('join', { conversationId });
            void this._refetchHistory(conversationId);
        };
        const onVisibilityChange = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                void this._refetchHistory(conversationId);
            }
        };

        socket.on('connect', onConnect);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }

        this._disposeReconnectWatch = () => {
            socket.off('connect', onConnect);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
        };
    }

    /**
     * Tear down: disconnect any open WS and restore captured console/fetch.
     */
    dispose(): void {
        this._disposeConsole?.();
        this._disposeFetch?.();
        this._disposeConsole = null;
        this._disposeFetch = null;
        this._started = false;

        this._disposeReconnectWatch?.();
        this._disposeReconnectWatch = null;

        if (this._typingStopTimer) {
            clearTimeout(this._typingStopTimer);
            this._typingStopTimer = null;
        }
        this._typingActive = false;

        if (this._socket) {
            this._socket.disconnect();
            this._socket = null;
        }
        this._conversationId = null;
        this._messagesById.clear();
        this._onHistory = null;
    }
}
